import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Project, RequestProps } from '../../../store/types'
import { useGetProjectsQuery } from '../../../store/redux/reducers/projectsSlice'
import { useSelector } from 'react-redux'
import { getCurrentUser, getIsLeaderAndNotManagement } from '../../../store/redux/reducers/currentUserSlice'
import { Box, Stack, TableCell, Typography } from '@mui/material'
import ReportsActionMenu from '../../Popup/ReportsActionMenu'
import { generatePath } from 'react-router-dom'
import navigation from '../../../config/navigation'
import AvatarComponent from '../../avatar/AvatarComponent'
import ProjectStatus from '../../../store/types/enums/ProjectStatus'
import ReportPopupData from '../../../store/types/utils/ReportPopupUtil'
import DataTable from '../DataTable'
import { StickyCell } from '../../../styles/WorklogTableStyles'
import { Column, Row } from '../../../store/types/utils/DataTableUtils'
import LocalStorageTypes from '../../../store/types/enums/LocalStorageTypes'
import SessionStorageKeys from '../../../store/types/enums/SessionStorageKeys'
import SortedLeaders from '../../../store/utils/SortedLeaders'
import usePersistedPage from '../../../store/utils/usePersistedPage'

const ReportsProjectsTable = ({
  searchInput,
  updateSelectedData,
  rowsPerPage,
  setRowsPerPage,
  isParentProjectsFilter,
}: {
  searchInput: string
  updateSelectedData: (selectedIds: ReportPopupData[]) => void
  rowsPerPage: number
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>
  isParentProjectsFilter: boolean
}) => {
  const { t } = useTranslation()
  const currentUser = useSelector(getCurrentUser)
  const isLeaderAndNotManagement = useSelector(getIsLeaderAndNotManagement)
  const [page, setPage] = usePersistedPage(SessionStorageKeys.ReportsProjectsTablePage)
  const [requestProps, setRequestProps] = useState<RequestProps>({
    page: page,
    size: rowsPerPage,
    'leaders.leader.id': isLeaderAndNotManagement ? currentUser?.id : undefined,
    parent: isParentProjectsFilter ? 'null' : undefined,
  })

  const { data: projectsResponse, isLoading } = useGetProjectsQuery(requestProps)

  useEffect(() => {
    setRequestProps(prevRequestProps => ({
      ...prevRequestProps,
      'query:name,acronym': searchInput || undefined,
      page: 0,
    }))
    setPage(0)
  }, [searchInput, setPage])

  const statusTranslationMapping = {
    [ProjectStatus.ACTIVE]: 'page.projects.filterPopup.active',
    [ProjectStatus.INACTIVE]: 'page.projects.filterPopup.inactive',
    [ProjectStatus.FINISHED]: 'page.projects.filterPopup.finished',
    [ProjectStatus.DELETED]: 'page.projects.filterPopup.deleted',
    [ProjectStatus.TRIAL]: 'page.projects.filterPopup.trial',
    [ProjectStatus.NONE_SELECTED]: 'page.projects.filterPopup.noneSelected',
  }

  const columns: Column[] = [
    { id: 'name', label: t('components.table.header.projectName') },
    { id: 'leaderLastNames', label: t('page.projects.filterPopup.leader') },
    { id: 'parent.acronym', label: t('page.projects.filterPopup.parent') },
    { id: 'projectStatus', label: t('components.table.header.status') },
    { id: 'action', label: t('components.table.header.action') },
  ]

  const extractData = (row: Row): Project =>
    ({
      ...row,
      favorite: row.favorite ?? false,
    } as Project)

  const renderTile = (project: Row, labelId: string, checkbox?: React.ReactNode) => {
    const projectData = extractData(project)
    const navigateString = generatePath(navigation.projectDetails, { id: projectData.id })

    return (
      <>
        <Stack direction="column" justifyContent="space-between" width="100%" key={labelId}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              {checkbox && checkbox}
              <Box
                sx={{
                  height: 'fit-content',
                  border: '1px solid',
                  borderRadius: 1,
                  p: '5px',
                  width: 'fit-content',
                  borderColor: 'background.elevation8',
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  {t(statusTranslationMapping[projectData.projectStatus as keyof typeof ProjectStatus])}
                </Typography>
              </Box>
            </Stack>
            <ReportsActionMenu navigationPath={navigateString} label={`${projectData.name} • ${projectData.acronym}`} />
          </Stack>
          <Stack direction="row" spacing={1} marginLeft="2px">
            <AvatarComponent
              avatar={projectData.avatar}
              extension={projectData.extension}
              alt={`${projectData.name}-project-avatar`}
              size={36}
            />
            <Stack direction="column" justifyContent="space-between">
              <Typography variant="h6">{project.acronym}</Typography>
              <Typography variant="subtitle1" component="div" sx={{ cursor: 'pointer' }}>
                {project.name}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        {projectData.leaders.length > 0 && (
          <Box marginTop="8px" width="fit-content" marginLeft="47px">
            <SortedLeaders leaders={projectData.leaders} />
          </Box>
        )}
        {projectData.parent?.acronym && (
          <Stack direction="row" spacing="5px" width="fit-content" marginTop="8px" marginLeft="47px">
            <Typography variant="body2" color="textSecondary" fontWeight={580}>
              {t('page.projects.filterPopup.parent')}:
            </Typography>
            <Typography variant="body2">{projectData.parent.acronym}</Typography>
          </Stack>
        )}
      </>
    )
  }

  const renderRow = (project: Row) => {
    const projectData = extractData(project)
    return (
      <>
        <TableCell>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                backgroundColor: 'background.main',
                color: 'text.base',
                p: 1,
                borderRadius: 6,
                justifyContent: 'center',
              }}
            >
              <AvatarComponent
                avatar={projectData.avatar}
                extension={projectData.extension}
                size={24}
                alt={`${projectData.name}--project-avatar`}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontWeight={700}>{project.acronym}</Typography>
              <Typography>{project.name}</Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <SortedLeaders leaders={projectData.leaders} />
        </TableCell>
        <TableCell>{project.parent?.acronym || '-'}</TableCell>
        <TableCell>{t(statusTranslationMapping[project.projectStatus as keyof typeof ProjectStatus])}</TableCell>
        <StickyCell
          align="center"
          sx={{
            borderLeft: '2px solid',
            borderLeftColor: 'background.elevation8',
            right: 0,
            '&:hover': {
              cursor: 'auto',
            },
          }}
        >
          <ReportsActionMenu
            navigationPath={generatePath(navigation.projectDetails, { id: project.id })}
            label={project.name + ' • ' + project.acronym}
          />
        </StickyCell>
      </>
    )
  }

  return (
    <DataTable
      unsortableColumns={[{ id: 'action', label: t('components.table.header.action') }]}
      isReportsView={true}
      renderTileCheckbox={true}
      renderTileContent={(project: Row, labelId: string, checkbox?: React.ReactNode) =>
        renderTile(project, labelId, checkbox)
      }
      singleSelection={false}
      updateSelectedData={updateSelectedData}
      columns={columns}
      data={projectsResponse?.content || []}
      renderRow={renderRow}
      totalElements={projectsResponse?.totalElements || 0}
      setRequestProps={setRequestProps}
      translationForNotFound={'page.worklog.form.projectsNotFound'}
      page={page}
      setPage={setPage}
      commonRowsPerPage={rowsPerPage}
      setCommonRowsPerPage={setRowsPerPage}
      isLoading={isLoading}
      localStorageRowsName={LocalStorageTypes.ReportsTableRows}
    />
  )
}

export default ReportsProjectsTable
